import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Stack, Typography } from '@mui/material'
import { TimeInMilliseconds, UnitTypes } from 'src/Libs/types'
import ChargingProgressBar from 'src/Components/charging-progress-bar/ChargingProgressBar'
import ToggleChargingSessionStatusButton from 'src/Libs/dashboard-widgets/widgets/wallbox/ToggleChargingSessionStatusButton'
import { J1ToprightarrowC, J2Caution } from '@e3dc-react/icons'
import { useTheme } from '@mui/system'
import useActiveChargingSessionStatus from 'src/Libs/dashboard-widgets/hooks/use-active-charging-session-status'
import { useTypedSelector } from 'src/State/RootReducer'
import { ChargingStatus } from 'src/Routes/EMobility/EMobilityModels'
import { isUndefined } from 'lodash'
import { VehicleImage } from '../vehicle-image'
import { ChargingStateType, VehicleChargingState } from '../../types'

interface VehicleDetailsProps {
  imageUrl?: string | null
  range: string
  currentSoc: string
  targetStateOfCharge: string
  status?: ChargingStateType
  vehicleAvailable: boolean
  wallboxIndex?: number
  wallboxChargingStatus?: ChargingStatus
  chargeLimitMax?: number
}

/**
 * VehicleDetailsCard shows charging details of current  user vehicle.
 * It allows the user to start and stop the current charging session
 */
const VehicleDetailsCard: React.FC<VehicleDetailsProps> = ({
  imageUrl,
  range,
  currentSoc,
  targetStateOfCharge,
  status,
  wallboxIndex,
  wallboxChargingStatus,
  vehicleAvailable,
  chargeLimitMax,
}) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const storageId = useTypedSelector(state => state.storage.activeStorage)

  const { isChargingSessionPaused, toggleWallboxChargingStatus } = useActiveChargingSessionStatus({
    wallboxIndex,
    storageId,
    refetchInterval: TimeInMilliseconds.tenSecond,
  })

  const showToggleChargingSessionStatusButton = !isUndefined(wallboxChargingStatus)

  const currentSocValueForProgressBar = currentSoc === '--' ? undefined : Number(currentSoc)
  const socValueForProgressBar = targetStateOfCharge === '--' ? undefined : Number(targetStateOfCharge)

  return (
    <Stack>
      <Stack direction="row" justifyContent="center" sx={{ width: '100%', overflow: 'hidden' }}>
        <VehicleImage imageUrl={imageUrl} showImage={vehicleAvailable} />
      </Stack>
      {vehicleAvailable && (
        <Box>
          <Stack direction="row" justifyContent="space-between" width="100%">
            <Stack direction="row" alignItems="baseline">
              <Typography variant="h1">
                {currentSoc}
                <Typography variant="h2" component="span">{` ${UnitTypes.percentage}`}</Typography>
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline">
              <Typography variant="h1">
                {range}
                <Typography variant="h2" component="span">{` ${UnitTypes.kilometer}`}</Typography>
              </Typography>
            </Stack>
          </Stack>

          {status === VehicleChargingState.fault && (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2">{t(`emobility.vehicle-integration.data.status.charging-error`)}</Typography>
              <J2Caution sx={{ height: 3, width: 3, marginLeft: '3px' }} />
            </Stack>
          )}
          {status && status !== VehicleChargingState.fault && (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2">{t(`emobility.vehicle-integration.data.status.${status.toLocaleLowerCase()}`)}</Typography>
              <J1ToprightarrowC sx={{ height: 3, width: 3, marginLeft: '3px' }} />
            </Stack>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: theme.spacing(2) }}>
            <ChargingProgressBar
              disabled={isChargingSessionPaused || status === undefined}
              discharging={false}
              currentChargeInPercentage={currentSocValueForProgressBar}
              chargeToInPercentage={socValueForProgressBar}
              stopped={status !== VehicleChargingState.charging}
              hasChargingError={status === VehicleChargingState.fault}
              markerInPercentage={chargeLimitMax}
            />
            {showToggleChargingSessionStatusButton && (
              <ToggleChargingSessionStatusButton
                chargingStatus={wallboxChargingStatus}
                isChargingSessionPaused={isChargingSessionPaused}
                toggleWallboxChargingStatus={toggleWallboxChargingStatus}
              />
            )}
          </Box>
        </Box>
      )}
    </Stack>
  )
}

export default VehicleDetailsCard
